import React, { useEffect, useRef } from "react";
import { Routes, Route, Navigate, BrowserRouter, useLocation } from "react-router-dom";
import "./toastStyles.css";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Import Datadog logging
import { initializeDatadogLogs, setDatadogUserContext } from "./utils/datadogLogger";
import LessonCom from "./components/lesson_Dashboard/LessonCom";
import CreateAccount from "./pages/auth/CreateAccount";
import CreateAccountForm from "./pages/CreateAccountForm";
import VerificationPage from "./pages/VerificationPage";
import LessonDashboard from "./components/lesson_Dashboard/LessonDashboard";
import TraceApp from "./components/Foundation/TraceCom";
import FoundationLessonCom from "./components/Foundation/FoundationLessonCom";
import SubscriptionPage from "./pages/payment";
import AfterLogin from "./pages/auth/AfterLogin";
import YearsDashboard from "./components/Years/YearsDashboard";
import SettingsDashboard from "./components/Profile/SettingsDashboard.js";
import ProfileEdit from "./components/Profile/ProfileEdit.js";
import BillingDetailsPage from "./components/Profile/BillingDetailsPage.jsx";
import About from "./assets/utils/About.js";
import FAQ from "./assets/utils/FAQ.js";
import ContactUs from "./components/Profile/ContactUs.js";
import YearSelection from "./components/Profile/YearSelection.js";
import ReportSecurityLayer from "./components/Report/Report.jsx";
import AIHandler from "./components/HandleAIQuestions/AIHandler.js";
import Entry from "./components/Entry";
import { Sounds } from "./assets/asset.js";
import { Provider, useSelector } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import store from "./store/store.js";
import LoginForm from "./pages/auth/LoginPage";
import Report from "./components/Leaderboard/ReportSection.jsx";
import Badges from "./components/Leaderboard/Badges.jsx";
import LogoutModal from "./components/LogoutModal.jsx";
import SessionTimeoutManager from "./components/SessionTimeoutManager.jsx";
import LeaderShipBoard from "./components/Leaderboard/LeadershipBoard.jsx";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import SubscriptionTermsPage from "./pages/SubscriptionTermsPage";
import SettingsTermsPolicyPage from "./pages/SettingsTermsPolicyPage";
import TutorInfoPage from "./pages/TutorInfoPage";
import TutorInfoDetailPage from "./pages/TutorInfoDetailPage";
import DeviceDetector from "./hooks/DeviceDetector.jsx";
import DemoDashboard from "./components/Years/DemoDashboard.js";
import AiParentsReport from "./components/Leaderboard/AiParentsReport.jsx";
import NotFoundPage from "./components/ui/NotFoundPage.jsx";
import ClassBookingDashboard from "./components/ClassBooking/Dashboard";
import BookTutor from "./components/ClassBooking/BookTutor";
import TutorDetails from "./components/ClassBooking/TutorDetails";
import BookingPage from "./components/ClassBooking/BookingPage";
import BookingConfirmed from "./components/ClassBooking/BookingConfirmed";
import MyBookings from "./components/ClassBooking/MyBookings";
import TeacherLogin from "./components/Teacher/TeacherLogin";
import TeacherDashboard from "./components/Teacher/TeacherDashboard";
import TeacherAvailability from "./components/Teacher/TeacherAvailability";
import TeacherProfile from "./components/Teacher/TeacherProfile";
import TeacherUpdateProfile from "./components/Teacher/TeacherUpdateProfile";
import TeacherSignup from "./components/Teacher/TeacherSignup";
import TeacherOtpVerification from "./components/Teacher/TeacherOtpVerification";
import TeacherForgotPassword from "./components/Teacher/TeacherForgotPassword";
import TeacherResetPassword from "./components/Teacher/TeacherResetPassword";
import TeacherList from "./components/Teacher/TeacherList";
import TutorDetail from "./components/Teacher/TutorDetail";
import AdminCreateTeacherAddress from "./components/Teacher/AdminCreateTeacherAddress";
import TeacherLogoutModal from "./components/Teacher/TeacherLogoutModal";
import TeacherSessionTimeoutManager from "./components/Teacher/TeacherSessionTimeoutManager";
import {
  TeacherPublicRoute,
  TeacherRouteGuard,
} from "./components/Teacher/TeacherRouteGuard";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import {
  AuthGuard,
  SubscriptionRouteGuard,
} from "./components/subscription/SubscriptionRouteGuard";
import {
  getAuthenticatedRedirectPath,
  isProfileIncomplete,
} from "./features/auth/profileCompletion";
import ParentLoginPage from "./pages/parent/ParentLoginPage";
import ParentSignupPage from "./pages/parent/ParentSignupPage";
import ParentVerificationPage from "./pages/parent/ParentVerificationPage";
import ParentForgotPasswordPage from "./pages/parent/ParentForgotPasswordPage";
import {
  PARENT_DEFAULT_REDIRECT_PATH,
  isParentSessionMode,
  readParentPostAuthRedirect,
} from "./features/parentFlow/parentFlow";

const queryClient = new QueryClient();

const isTeacherFlowPath = (pathname = "") =>
  pathname.startsWith("/teacher") ||
  pathname === "/reset-password" ||
  pathname.startsWith("/admin/teacher-address");

const PublicRoute = ({ element }) => {
  const { user, token } = useSelector((state) => state.user);
  if (!token) return element;

  if (isParentSessionMode()) {
    return (
      <Navigate
        to={readParentPostAuthRedirect(PARENT_DEFAULT_REDIRECT_PATH)}
        replace
      />
    );
  }

  return <Navigate to={getAuthenticatedRedirectPath(user)} replace />;
};

const ParentPublicRoute = ({ element }) => {
  const { user, token } = useSelector((state) => state.user);
  if (!token) return element;

  if (isProfileIncomplete(user)) {
    return <Navigate to={getAuthenticatedRedirectPath(user)} replace />;
  }

  return (
    <Navigate
      to={readParentPostAuthRedirect(PARENT_DEFAULT_REDIRECT_PATH)}
      replace
    />
  );
};

const IndexRoute = () => {
  const { user, token } = useSelector((state) => state.user);
  if (!token) return <Entry />;

  if (isParentSessionMode()) {
    return (
      <Navigate
        to={readParentPostAuthRedirect(PARENT_DEFAULT_REDIRECT_PATH)}
        replace
      />
    );
  }

  return <Navigate to={getAuthenticatedRedirectPath(user)} replace />;
};

function App() {
  const location = useLocation();
  const audioRef = useRef(new Audio(Sounds.backgroundMusic));
  const { showLogoutModal, user } = useSelector((state) => state.user);
  const showTeacherLogoutModal = useSelector(
    (state) => state.teacher?.showLogoutModal
  );
  const { isMuted } = useSelector((state) => state.mute);
  const isTeacherFlow = isTeacherFlowPath(location.pathname);

  // Initialize Datadog on app load
  useEffect(() => {
    initializeDatadogLogs();
  }, []);

  // Set user context when user logs in
  useEffect(() => {
    if (user) {
      setDatadogUserContext(user);
    }
  }, [user]);

  useEffect(() => {
    const audio = audioRef.current;
    audio.loop = true;

    if (!isMuted) {
      audio.play().catch((error) => {
        console.log("Audio playback failed:", error);
      });
    } else {
      audio.pause();
    }

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [isMuted]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#d7f6f7]">
      <DeviceDetector />
      {!isTeacherFlow && <SessionTimeoutManager />}
      {isTeacherFlow && <TeacherSessionTimeoutManager />}
      {!isTeacherFlow && showLogoutModal && <LogoutModal />}
      {showTeacherLogoutModal && isTeacherFlow && <TeacherLogoutModal />}
      <Routes>
        <Route index element={<IndexRoute />} />
        <Route path="/tutorinfo" element={<TutorInfoPage />} />
        <Route path="/tutorinfo/:tutorId" element={<TutorInfoDetailPage />} />
        <Route
          path="/createaccount"
          element={<PublicRoute element={<CreateAccount />} />}
        />
        <Route
          path="/createaccountform"
          element={<PublicRoute element={<CreateAccountForm />} />}
        />
        <Route
          path="/verification"
          element={<PublicRoute element={<VerificationPage />} />}
        />
        <Route
          path="/login"
          element={<PublicRoute element={<LoginForm />} />}
        />
        <Route
          path="/parent/login"
          element={<ParentPublicRoute element={<ParentLoginPage />} />}
        />
        <Route
          path="/parent/signup"
          element={<ParentPublicRoute element={<ParentSignupPage />} />}
        />
        <Route
          path="/parent/verification"
          element={<ParentPublicRoute element={<ParentVerificationPage />} />}
        />
        <Route
          path="/parent/forgot-password"
          element={<ParentPublicRoute element={<ParentForgotPasswordPage />} />}
        />
        <Route
          path="/forgot-password"
          element={<PublicRoute element={<ForgotPasswordPage />} />}
        />
        <Route
          path="/teacherlogin"
          element={<TeacherPublicRoute element={<TeacherLogin />} />}
        />
        <Route
          path="/teachersignup"
          element={<TeacherPublicRoute element={<TeacherSignup />} />}
        />
        <Route
          path="/teacherverify-otp"
          element={<TeacherPublicRoute element={<TeacherOtpVerification />} />}
        />
        <Route
          path="/teacherforgot-password"
          element={<TeacherPublicRoute element={<TeacherForgotPassword />} />}
        />
        <Route path="/reset-password" element={<TeacherResetPassword />} />
        <Route path="/teachers" element={<TeacherList />} />
        <Route path="/tutors/:tutorId" element={<TutorDetail />} />
        <Route path="/admin/teacher-address" element={<AdminCreateTeacherAddress />} />
        <Route
          path="/teacherdashboard"
          element={<TeacherRouteGuard element={<TeacherDashboard />} />}
        />
        <Route
          path="/teacheravailability"
          element={<TeacherRouteGuard element={<TeacherAvailability />} />}
        />
        <Route
          path="/teacherprofile"
          element={<TeacherRouteGuard element={<TeacherProfile />} />}
        />
        <Route
          path="/teacherprofile/update"
          element={<TeacherRouteGuard element={<TeacherUpdateProfile />} />}
        />
        <Route path="/contact" element={<ContactUs />} />

        {/* Protected Routes */}
        <Route
          path="/afterlogin"
          element={<AuthGuard element={<AfterLogin />} />}
        />
        <Route
          path="/yearsdashboard"
          element={<AuthGuard element={<YearsDashboard />} />}
        />
        <Route
          path="/lessonCom"
          element={<SubscriptionRouteGuard element={<LessonCom />} />}
        />
        <Route
          path="/lessonDashboard"
          element={
            <SubscriptionRouteGuard
              element={<LessonDashboard />}
              restrictBasicToCurrentYear
            />
          }
        />
        <Route
          path="/trace"
          element={<SubscriptionRouteGuard element={<TraceApp />} />}
        />
        <Route
          path="/foundationLessonCom"
          element={
            <SubscriptionRouteGuard
              element={<FoundationLessonCom />}
              restrictBasicToCurrentYear
            />
          }
        />
        <Route
          path="/subscription"
          element={<AuthGuard element={<SubscriptionPage />} />}
        />
        <Route
          path="/subscription-terms"
          element={<SubscriptionTermsPage />}
        />
        <Route
          path="/settings-terms-policy"
          element={<SettingsTermsPolicyPage />}
        />
        <Route
          path="/settings"
          element={
            <SubscriptionRouteGuard
              element={<SettingsDashboard />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/billing-details"
          element={<AuthGuard element={<BillingDetailsPage />} />}
        />
        <Route
          path="/editProffile"
          element={
            <SubscriptionRouteGuard
              element={<ProfileEdit />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/about"
          element={<SubscriptionRouteGuard element={<About />} allowTrialOrActive />}
        />
        <Route
          path="/faq"
          element={<SubscriptionRouteGuard element={<FAQ />} />}
        />

        <Route
          path="/report"
          element={
            <SubscriptionRouteGuard
              element={<ReportSecurityLayer />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/selectYear"
          element={<SubscriptionRouteGuard element={<YearSelection />} />}
        />
        <Route
          path="/AIHandler/:yearId"
          element={
            <SubscriptionRouteGuard
              element={<AIHandler />}
              requiredPlanType="pro"
            />
          }
        />
        <Route
          path="/report/details"
          element={<SubscriptionRouteGuard element={<Report />} allowTrialOrActive />}
        />
        <Route
          path="report/ai-report"
          element={
            <SubscriptionRouteGuard
              element={<AiParentsReport />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/report/badges"
          element={<SubscriptionRouteGuard element={<Badges />} allowTrialOrActive />}
        />
        <Route
          path="/leaderboard"
          element={
            <SubscriptionRouteGuard
              element={<LeaderShipBoard />}
              allowTrialOrActive
            />
          }
        />
        <Route
          path="/demoDashboard"
          element={
            <SubscriptionRouteGuard
              element={<DemoDashboard />}
              restrictBasicToCurrentYear
            />
          }
        />
        <Route
          path="/classBooking/dashboard"
          element={<SubscriptionRouteGuard element={<ClassBookingDashboard />} />}
        />
        <Route
          path="/parent/classBooking/dashboard"
          element={<SubscriptionRouteGuard element={<ClassBookingDashboard />} />}
        />
        <Route
          path="/classBooking/bookTutor"
          element={<SubscriptionRouteGuard element={<BookTutor />} />}
        />
        <Route
          path="/parent/classBooking/bookTutor"
          element={<SubscriptionRouteGuard element={<BookTutor />} />}
        />
        <Route
          path="/classBooking/tutorDetails/:tutorId"
          element={<SubscriptionRouteGuard element={<TutorDetails />} />}
        />
        <Route
          path="/parent/classBooking/tutorDetails/:tutorId"
          element={<SubscriptionRouteGuard element={<TutorDetails />} />}
        />
        <Route
          path="/classBooking/booking"
          element={<SubscriptionRouteGuard element={<BookingPage />} />}
        />
        <Route
          path="/parent/classBooking/booking"
          element={<SubscriptionRouteGuard element={<BookingPage />} />}
        />
        <Route
          path="/classBooking/bookingConfirmed"
          element={<SubscriptionRouteGuard element={<BookingConfirmed />} />}
        />
        <Route
          path="/parent/classBooking/bookingConfirmed"
          element={<SubscriptionRouteGuard element={<BookingConfirmed />} />}
        />
        <Route
          path="/classBooking/myBookings"
          element={<SubscriptionRouteGuard element={<MyBookings />} />}
        />
        <Route
          path="/parent/classBooking/myBookings"
          element={<SubscriptionRouteGuard element={<MyBookings />} />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

const AppWrapper = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SubscriptionProvider>
            <App />
          </SubscriptionProvider>
               <ToastContainer
            toastClassName="global-toast-bigger font-mali"
            bodyClassName="global-toast-body"
            progressClassName="global-toast-progress"
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
};

export default AppWrapper;
